<script setup>
/**
 * Fluxo de criação de conexão WhatsApp, espelhando o assistente do Aceleres
 * (/integracoes): nome → tipo/API → aviso de versão → pareamento.
 *
 * O hub usava um formulário inline com selects crus: o gestor escolhia
 * "Individual da empresa" ou "API 1" sem nenhuma explicação do que cada opção
 * significa, e nada avisava que um WhatsApp desatualizado derruba o
 * pareamento. As três etapas abaixo existem para resolver exatamente isso.
 */
import { computed, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  sellers: {
    type: Array,
    default: () => [],
  },
  providers: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['create', 'close']);

const dialogRef = ref(null);

const STEPS = [
  { key: 'name', label: 'Nome' },
  { key: 'type', label: 'Tipo e API' },
  { key: 'warning', label: 'Antes de conectar' },
];

// Espelha os cartões do Aceleres. `shared` não aparece na criação lá e também
// não aparece aqui — continua disponível na edição para conexões antigas.
const CONNECTION_TYPES = [
  {
    value: 'individual_own',
    title: 'Número pessoal',
    icon: 'i-lucide-lock',
    iconClass: 'bg-n-blue-3 text-n-blue-11',
    description:
      'WhatsApp pessoal do usuário. Somente o proprietário acessa as conversas — o gestor não vê nada por padrão. O gestor só visualiza as conversas que o próprio usuário definir como atendimento da empresa.',
  },
  {
    value: 'individual_company',
    title: 'Corporativo individual',
    icon: 'i-lucide-building-2',
    iconClass: 'bg-n-amber-3 text-n-amber-11',
    description:
      'Número oficial de propriedade da empresa, com acompanhamento do gestor e responsável definido.',
  },
  {
    value: 'company_multi',
    title: 'Corporativo compartilhado',
    icon: 'i-lucide-users',
    iconClass: 'bg-n-teal-3 text-n-teal-11',
    description:
      'Número disponível para a equipe, com atendimento compartilhado e rodízio automático.',
  },
];

// Fallback para o caso de o bridge do Aceleres não informar os providers.
// A regra é a mesma de lá: a Evolution só reaparece se a WAME não existir.
const FALLBACK_PROVIDERS = [
  {
    key: 'api2',
    label: 'API2 / WAME',
    description: 'Conexão pelo Servidor Estável Beta.',
  },
  {
    key: 'zapi',
    label: 'Z-API',
    description: 'Conexão pela integração Z-API.',
  },
];

const stepIndex = ref(0);
const displayName = ref('');
const connectionType = ref('individual_own');
const assigneeUserId = ref('');
const provider = ref('');
const error = ref('');

const currentStep = computed(() => STEPS[stepIndex.value].key);

const availableProviders = computed(() =>
  props.providers.length ? props.providers : FALLBACK_PROVIDERS
);

const requiresAssignee = computed(
  () => connectionType.value === 'individual_company'
);

const sellerOptions = computed(() => props.sellers || []);

const reset = () => {
  stepIndex.value = 0;
  displayName.value = '';
  connectionType.value = 'individual_own';
  assigneeUserId.value = '';
  // Um único provider disponível não é uma escolha: já vem marcado.
  provider.value =
    availableProviders.value.length === 1
      ? availableProviders.value[0].key
      : '';
  error.value = '';
};

const open = () => {
  reset();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

defineExpose({ open, close });

watch(
  () => connectionType.value,
  () => {
    error.value = '';
  }
);

const goBack = () => {
  error.value = '';
  if (stepIndex.value === 0) {
    close();
    return;
  }
  stepIndex.value -= 1;
};

const submit = () => {
  emit('create', {
    display_name: displayName.value.trim(),
    connection_type: connectionType.value,
    default_assignee_user_id: requiresAssignee.value
      ? Number(assigneeUserId.value)
      : null,
    connection_api: provider.value,
  });
};

const advance = () => {
  if (currentStep.value === 'name') {
    if (!displayName.value.trim()) {
      error.value = 'Informe um nome para a inbox.';
      return;
    }
    error.value = '';
    stepIndex.value = 1;
    return;
  }

  if (currentStep.value === 'type') {
    if (!provider.value) {
      error.value = 'Escolha uma API para continuar.';
      return;
    }
    if (requiresAssignee.value && !assigneeUserId.value) {
      error.value =
        'Escolha o vendedor responsável pelo número corporativo individual.';
      return;
    }
    error.value = '';
    stepIndex.value = 2;
    return;
  }

  submit();
};

const primaryLabel = computed(() => {
  if (currentStep.value === 'name') return 'Continuar';
  if (currentStep.value === 'type') return 'Continuar';
  return 'Entendi, continuar';
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <Dialog
    ref="dialogRef"
    width="xl"
    position="top"
    overflow-y-auto
    show-close-button
    @close="emit('close')"
  >
    <div class="flex flex-col gap-5">
      <!-- Stepper: sem ele o usuário não sabe quantas telas faltam. -->
      <ol class="flex items-center gap-2">
        <li
          v-for="(step, index) in STEPS"
          :key="step.key"
          class="flex flex-1 items-center gap-2"
        >
          <span
            class="flex size-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors"
            :class="
              index <= stepIndex
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-2 text-n-slate-10'
            "
          >
            <i v-if="index < stepIndex" class="i-lucide-check text-sm" />
            <template v-else>{{ index + 1 }}</template>
          </span>
          <span
            class="truncate text-xs font-medium"
            :class="index <= stepIndex ? 'text-n-slate-12' : 'text-n-slate-10'"
          >
            {{ step.label }}
          </span>
          <span
            v-if="index < STEPS.length - 1"
            class="h-px flex-1 bg-n-weak"
            aria-hidden="true"
          />
        </li>
      </ol>

      <!-- Etapa 1 — nome -->
      <div v-if="currentStep === 'name'" class="flex flex-col gap-4">
        <div class="flex flex-col items-center gap-3 text-center">
          <div
            class="flex size-12 items-center justify-center rounded-2xl bg-n-brand/10 text-n-brand"
          >
            <i class="i-lucide-box text-2xl" />
          </div>
          <h3 class="text-lg font-semibold text-n-slate-12">
            Nova inbox de WhatsApp
          </h3>
          <p class="text-sm leading-6 text-n-slate-11">
            Uma inbox é a caixa de entrada de um número de WhatsApp: todas as
            conversas recebidas nesse número chegam nela, no painel de
            atendimento, para você e sua equipe responderem. Após dar um nome,
            você escolhe o tipo de conexão e conecta o número escaneando um QR
            Code.
          </p>
        </div>

        <label class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            Nome da inbox
          </span>
          <input
            v-model="displayName"
            class="rounded-xl border border-n-weak bg-n-background px-3 py-2.5 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand"
            maxlength="60"
            placeholder="Ex.: Vendas, Suporte, WhatsApp Aceleres"
            @keyup.enter="advance"
          />
        </label>

        <p
          class="flex items-start gap-2 rounded-xl bg-n-alpha-1 px-3 py-2.5 text-xs leading-5 text-n-slate-11"
        >
          <i class="i-lucide-info mt-0.5 flex-shrink-0 text-sm" />
          <span>
            <strong class="text-n-slate-12">Dica</strong> — use um nome que
            facilite a identificação da inbox pela sua equipe. Você pode
            renomeá-la depois.
          </span>
        </p>
      </div>

      <!-- Etapa 2 — tipo de conexão e API -->
      <div v-else-if="currentStep === 'type'" class="flex flex-col gap-4">
        <div class="flex flex-col items-center gap-2 text-center">
          <div
            class="flex size-12 items-center justify-center rounded-2xl bg-n-brand/10 text-n-brand"
          >
            <i class="i-lucide-git-branch text-2xl" />
          </div>
          <h3 class="text-lg font-semibold text-n-slate-12">Tipo de conexão</h3>
          <p class="text-sm text-n-slate-11">
            Como este número WhatsApp será usado?
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <button
            v-for="type in CONNECTION_TYPES"
            :key="type.value"
            type="button"
            class="flex w-full items-start gap-3 rounded-2xl border p-3 text-start transition-colors"
            :class="
              connectionType === type.value
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak hover:bg-n-alpha-1'
            "
            @click="connectionType = type.value"
          >
            <span
              class="flex size-9 flex-shrink-0 items-center justify-center rounded-xl"
              :class="type.iconClass"
            >
              <i :class="type.icon" class="text-lg" />
            </span>
            <span class="min-w-0 flex-1">
              <span class="block text-sm font-semibold text-n-slate-12">
                {{ type.title }}
              </span>
              <span class="mt-1 block text-xs leading-5 text-n-slate-11">
                {{ type.description }}
              </span>
            </span>
            <span
              class="mt-1 flex size-4 flex-shrink-0 items-center justify-center rounded-full border"
              :class="
                connectionType === type.value
                  ? 'border-n-brand bg-n-brand'
                  : 'border-n-weak'
              "
            >
              <i
                v-if="connectionType === type.value"
                class="i-lucide-check text-[10px] text-white"
              />
            </span>
          </button>
        </div>

        <!-- Fora do cartão de propósito: um <select> dentro de um <button> é
             HTML inválido e o clique abriria o cartão em vez do menu. -->
        <label v-if="requiresAssignee" class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            Atribuir automaticamente a:
          </span>
          <select
            v-model="assigneeUserId"
            class="rounded-xl border border-n-weak bg-n-background px-3 py-2.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option value="">Selecione um vendedor</option>
            <option
              v-for="seller in sellerOptions"
              :key="seller.id"
              :value="seller.id"
            >
              {{ seller.name }}
            </option>
          </select>
          <span v-if="!sellerOptions.length" class="text-xs text-n-amber-11">
            Nenhum vendedor disponível para atribuição.
          </span>
        </label>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            Selecione a API de conexão
          </span>
          <div class="flex flex-wrap gap-2">
            <!--
              A marca de selecionado usa `n-teal` porque **não existe escala
              `n-green` neste design system** — `border-n-green-7 bg-n-green-2`
              não gerava CSS nenhum, e clicar em API2 ou Z-API não mudava nada na
              tela. Confirme antes de trocar de cor:
              `grep -c -- "bg-n-<cor>-2" public/vite/assets/dashboard-*.css`
              — classe usada e não gerada é classe inexistente.

              O anel e o ícone existem porque só a borda era fraca demais para
              indicar escolha num grupo de dois cartões.
            -->
            <button
              v-for="item in availableProviders"
              :key="item.key"
              type="button"
              :aria-pressed="provider === item.key"
              class="flex min-w-[9rem] flex-1 flex-col gap-0.5 rounded-xl border p-3 text-start transition-colors"
              :class="
                provider === item.key
                  ? 'border-n-teal-7 bg-n-teal-2 ring-2 ring-n-teal-7'
                  : 'border-n-weak hover:bg-n-alpha-1'
              "
              @click="provider = item.key"
            >
              <span
                class="flex items-center gap-1.5 text-sm font-semibold text-n-slate-12"
              >
                <i
                  v-if="provider === item.key"
                  class="i-lucide-check-circle-2 text-n-teal-11"
                />
                {{ item.label }}
              </span>
              <span class="text-xs leading-5 text-n-slate-11">
                {{ item.description }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Etapa 3 — aviso obrigatório de versão do WhatsApp -->
      <div v-else class="flex flex-col gap-4">
        <div class="flex flex-col items-center gap-2 text-center">
          <div
            class="flex size-12 items-center justify-center rounded-2xl bg-n-teal-3 text-n-teal-11"
          >
            <i class="i-ri-whatsapp-line text-2xl" />
          </div>
          <h3 class="text-lg font-semibold text-n-slate-12">
            Mantenha o
            <span class="text-n-ruby-11">WhatsApp</span> sempre atualizado
          </h3>
          <p class="text-sm text-n-slate-11">
            Para evitar erros de conexão, é <strong>obrigatório</strong>
            utilizar a versão mais recente do WhatsApp.
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <div
            class="flex items-start gap-3 rounded-2xl border border-n-weak p-3"
          >
            <span
              class="flex size-9 flex-shrink-0 items-center justify-center rounded-xl bg-n-blue-3 text-n-blue-11"
            >
              <i class="i-lucide-shield-check text-lg" />
            </span>
            <span>
              <span class="block text-sm font-semibold text-n-slate-12">
                Por que isso é importante?
              </span>
              <span class="mt-1 block text-xs leading-5 text-n-slate-11">
                Versões desatualizadas podem causar falhas na leitura do QR
                Code, desconexões e perda de mensagens.
              </span>
            </span>
          </div>
          <div
            class="flex items-start gap-3 rounded-2xl border border-n-weak p-3"
          >
            <span
              class="flex size-9 flex-shrink-0 items-center justify-center rounded-xl bg-n-amber-3 text-n-amber-11"
            >
              <i class="i-lucide-refresh-cw text-lg" />
            </span>
            <span>
              <span class="block text-sm font-semibold text-n-slate-12">
                Atualize agora
              </span>
              <span class="mt-1 block text-xs leading-5 text-n-slate-11">
                Abra a loja de apps no seu celular e verifique se há
                atualizações disponíveis do WhatsApp.
              </span>
            </span>
          </div>
        </div>

        <div
          class="rounded-2xl border border-n-weak bg-n-alpha-1 px-3 py-2.5 text-xs leading-5 text-n-slate-11"
        >
          <strong class="text-n-slate-12">{{ displayName }}</strong>
          será criada como
          <strong class="text-n-slate-12">
            {{
              CONNECTION_TYPES.find(item => item.value === connectionType)
                ?.title
            }}
          </strong>
          usando
          <strong class="text-n-slate-12">
            {{
              availableProviders.find(item => item.key === provider)?.label ||
              provider
            }}
          </strong>
          . Em seguida você lê o QR Code para parear o número.
        </div>
      </div>

      <p
        v-if="error"
        class="flex items-start gap-2 text-xs font-medium text-n-ruby-11"
      >
        <i class="i-lucide-circle-alert mt-0.5 text-sm" />
        {{ error }}
      </p>
    </div>

    <template #footer>
      <div class="flex items-center gap-3">
        <Button
          type="button"
          variant="faded"
          color="slate"
          class="w-full"
          :label="stepIndex === 0 ? 'Cancelar' : 'Voltar'"
          @click="goBack"
        />
        <Button
          type="button"
          class="w-full"
          :label="primaryLabel"
          :is-loading="isLoading"
          :disabled="isLoading"
          @click="advance"
        />
      </div>
      <p
        class="mt-3 flex items-center justify-center gap-1.5 text-xs text-n-slate-10"
      >
        <i class="i-lucide-lock text-xs" />
        Seus dados estão protegidos com segurança de ponta.
      </p>
    </template>
  </Dialog>
</template>
